import { useIsMobile } from '@/hooks/useIsMobile';
import { useTRPC } from '@/utils/trpc';
import { Button } from '@liza/ui/components/ui/button';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@liza/ui/components/ui/resizable';
import { Spinner } from '@liza/ui/components/ui/spinner';
import { cn } from '@liza/ui/utils';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import { Allergies } from '../RecordDetails/Allergies';
import { CareTeam } from '../RecordDetails/CareTeam';
import { Conditions } from '../RecordDetails/Conditions';
import { Documents } from '../RecordDetails/Documents';
import { Encounters } from '../RecordDetails/Encounters';
import { FamilyHistory } from '../RecordDetails/FamilyHistory';
import { Immunizations } from '../RecordDetails/Immunizations';
import { LabOrders } from '../RecordDetails/LabOrders';
import { Labs } from '../RecordDetails/Labs';
import { Medications } from '../RecordDetails/Medications';
import { Procedures } from '../RecordDetails/Procedures';
import { ServiceRequests } from '../RecordDetails/ServiceRequests';
import { SocialHistory } from '../RecordDetails/SocialHistory';
import { Surveys } from '../RecordDetails/Surveys';
import { Vitals } from '../RecordDetails/Vitals';
import type { DetailPanelView } from '../types';
import { DetailColumn } from './DetailColumn';
import { hasDataSelection } from './hasDataSelection';
import { RESOURCE_ICONS } from './resourceIcons';
import { ResourceNavButton } from './ResourceNavButton';
import { dataResources, type DataResource } from './types';
import { useControlBarClearance } from '../useControlBarClearance';

/** The sidebar dividers' resize affordance (see `SidebarResizeHandle`): the divider thickens to 3px
 * and takes the primary color on hover/keyboard focus. Styles the handle's absolutely-positioned
 * `after` overlay, so the thickening causes no layout shift. */
const resizeHoverAffordanceClassName =
  'after:transition-all after:duration-150 hover:after:w-[3px] hover:after:bg-primary focus-visible:after:w-[3px] focus-visible:after:bg-primary';

type Props = {
  patientId: string;
  resource: DataResource;
  selectedId?: string;
  labIdentifier?: string;
  vitalIdentifier?: string;
  surveyIdentifier?: string;
  medicationResourceType?: 'MedicationRequest' | 'MedicationStatement';
  onSelectResource: (resource: DataResource) => void;
  onSelectItem: (opts: {
    id?: string;
    labIdentifier?: string;
    vitalIdentifier?: string;
    surveyIdentifier?: string;
    medicationResourceType?: 'MedicationRequest' | 'MedicationStatement';
  }) => void;
  /** Switch resource type and select an item in one navigation (cross-resource links). */
  onNavigateToResource: (opts: { resource: DataResource; id?: string; labIdentifier?: string }) => void;
};

export const DataTab = ({
  patientId,
  resource,
  selectedId,
  labIdentifier,
  vitalIdentifier,
  surveyIdentifier,
  medicationResourceType,
  onSelectResource,
  onSelectItem,
  onNavigateToResource,
}: Props): React.ReactNode => {
  const trpc = useTRPC();
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const clearance = useControlBarClearance();
  const patientQuery = useQuery(trpc.patient.get.queryOptions({ id: patientId }));
  const patient = patientQuery.data?.patient;

  if (patientQuery.isPending || !patient) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  const handleListViewChange = (view: DetailPanelView | null): void => {
    if (!view) {
      onSelectItem({});
      return;
    }
    match(view)
      .with({ type: 'condition-detail' }, ({ id }) => onSelectItem({ id }))
      .with({ type: 'document-detail' }, ({ id }) => onSelectItem({ id }))
      .with({ type: 'medication-detail' }, ({ id, resourceType }) =>
        onSelectItem({ id, medicationResourceType: resourceType }),
      )
      .with({ type: 'allergy-detail' }, ({ id }) => onSelectItem({ id }))
      .with({ type: 'immunization-detail' }, ({ id }) => onSelectItem({ id }))
      .with({ type: 'encounter-detail' }, ({ id }) => onSelectItem({ id }))
      .with({ type: 'procedure-detail' }, ({ id }) => onSelectItem({ id }))
      .with({ type: 'family-member-detail' }, ({ id }) => onSelectItem({ id }))
      .with({ type: 'lab-detail' }, ({ labIdentifier: labId }) => onSelectItem({ labIdentifier: labId }))
      .with({ type: 'vital-detail' }, ({ vitalIdentifier: vitalId }) => onSelectItem({ vitalIdentifier: vitalId }))
      .with({ type: 'survey-detail' }, ({ surveyIdentifier: surveyId }) => onSelectItem({ surveyIdentifier: surveyId }))
      .with({ type: 'service-request-detail' }, ({ id }) => onSelectItem({ id }))
      .with({ type: 'lab-order-detail' }, ({ id }) => onSelectItem({ id }))
      .otherwise(() => onSelectItem({}));
  };

  const recordList = match(resource)
    .with('careTeam', () => <CareTeam patient={patient} />)
    .with('conditions', () => (
      <Conditions patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .with('documents', () => (
      <Documents patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .with('medications', () => (
      <Medications
        patient={patient}
        onViewChange={handleListViewChange}
        selectedId={selectedId}
        selectedResourceType={medicationResourceType}
      />
    ))
    .with('allergies', () => (
      <Allergies patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .with('immunizations', () => (
      <Immunizations patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .with('labs', () => (
      <Labs patient={patient} onViewChange={handleListViewChange} selectedLabIdentifier={labIdentifier} />
    ))
    .with('vitals', () => (
      <Vitals patient={patient} onViewChange={handleListViewChange} selectedVitalIdentifier={vitalIdentifier} />
    ))
    .with('encounters', () => (
      <Encounters patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .with('procedures', () => (
      <Procedures patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .with('familyHistory', () => (
      <FamilyHistory patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .with('socialHistory', () => <SocialHistory patient={patient} />)
    .with('surveys', () => (
      <Surveys patient={patient} onViewChange={handleListViewChange} selectedSurveyIdentifier={surveyIdentifier} />
    ))
    .with('serviceRequests', () => (
      <ServiceRequests patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .with('labOrders', () => (
      <LabOrders patient={patient} onViewChange={handleListViewChange} selectedId={selectedId} />
    ))
    .exhaustive();

  if (isMobile) {
    const detailOpen = Boolean(selectedId || labIdentifier || vitalIdentifier || surveyIdentifier);
    if (detailOpen) {
      return (
        <div className="flex h-full flex-col">
          <div className="flex shrink-0 items-center gap-1 border-b border-border bg-card px-2 py-1.5">
            <Button variant="ghost" size="sm" onClick={() => onSelectItem({})} aria-label={t(resource)}>
              <ChevronLeftIcon className="size-4" />
              {t(resource)}
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto bg-card/30">
            <DetailColumn
              resource={resource}
              selectedId={selectedId}
              labIdentifier={labIdentifier}
              vitalIdentifier={vitalIdentifier}
              surveyIdentifier={surveyIdentifier}
              medicationResourceType={medicationResourceType}
              medplumPatientId={patient.medplumId}
              onNavigateToResource={onNavigateToResource}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="flex h-full flex-col">
        <nav className="-mx-2 shrink-0 overflow-x-auto border-b border-border bg-card p-2" aria-label={t('navigation')}>
          <div className="flex w-fit items-center gap-1 whitespace-nowrap">
            {dataResources.map((r) => (
              <Button
                key={r}
                variant="ghost"
                size="sm"
                onClick={() => onSelectResource(r)}
                aria-current={r === resource ? 'page' : undefined}
                className={cn(
                  'rounded-full font-medium',
                  r === resource
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )}
              >
                {t(r)}
              </Button>
            ))}
          </div>
        </nav>
        <div className="min-h-0 flex-1 overflow-y-auto bg-sidebar">{recordList}</div>
      </div>
    );
  }

  return (
    <ResizablePanelGroup orientation="horizontal" aria-label="Data browser">
      <ResizablePanel
        id="data-resources"
        groupResizeBehavior="preserve-pixel-size"
        defaultSize="220px"
        minSize="180px"
        aria-label="Resource types"
      >
        <div className={`h-full overflow-y-auto bg-sidebar px-2 pt-2 ${clearance}`}>
          {dataResources.map((r) => (
            <ResourceNavButton
              key={r}
              label={t(r)}
              icon={RESOURCE_ICONS[r]}
              active={r === resource}
              onClick={() => onSelectResource(r)}
            />
          ))}
        </div>
      </ResizablePanel>
      <ResizableHandle className={resizeHoverAffordanceClassName} />
      <ResizablePanel id="data-workspace">
        <ResizablePanelGroup orientation="horizontal">
          <ResizablePanel id="data-list" defaultSize={40} aria-label="Resource list">
            <div className={`h-full overflow-y-auto bg-sidebar ${clearance}`}>{recordList}</div>
          </ResizablePanel>
          <ResizableHandle className={resizeHoverAffordanceClassName} />
          <ResizablePanel id="data-detail" defaultSize={60} aria-label="Resource detail">
            <div
              className={cn(
                'h-full overflow-y-auto bg-card/30',
                // Only once there is a record in it: see `hasDataSelection`.
                hasDataSelection({ resource, selectedId, labIdentifier, vitalIdentifier, surveyIdentifier }) &&
                  clearance,
              )}
            >
              <DetailColumn
                resource={resource}
                selectedId={selectedId}
                labIdentifier={labIdentifier}
                vitalIdentifier={vitalIdentifier}
                surveyIdentifier={surveyIdentifier}
                medicationResourceType={medicationResourceType}
                medplumPatientId={patient.medplumId}
                onNavigateToResource={onNavigateToResource}
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};
